"use client";

import { motion } from "framer-motion";
import { DollarSign, Download, FileSpreadsheet, Filter, PiggyBank, Target, TrendingDown, TrendingUp, Wallet, Car } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { AppShellMock } from "./AppShellMock";

const BAR_HEIGHTS = [32, 48, 42, 58, 52, 72, 88, 76, 64, 70] as const;

const KPI_MOCK = [
  { label: "Receita Total", value: "R$ 142.590", icon: DollarSign, color: "text-emerald-500", bg: "bg-emerald-500/10" },
  { label: "Despesas Totais", value: "R$ 98.320", icon: TrendingDown, color: "text-red-500", bg: "bg-red-500/10" },
  { label: "Saldo em Caixa", value: "R$ 44.270", icon: Wallet, color: "text-primary", bg: "bg-primary/10" },
  { label: "Lucro Líquido", value: "R$ 32.180", icon: TrendingUp, color: "text-emerald-500", bg: "bg-emerald-500/10" },
  { label: "Margem de Lucro", value: "18,4%", icon: Target, color: "text-blue-500", bg: "bg-blue-500/10" },
  { label: "Vendas no Mês", value: "7", icon: PiggyBank, color: "text-purple-500", bg: "bg-purple-500/10" },
] as const;

const LANCS = [
  { d: "02/04", desc: "Venda veículo — rec. loja", v: "+ R$ 82.500", pos: true },
  { d: "01/04", desc: "Despesa preparação HB20", v: "- R$ 1.200", pos: false },
  { d: "31/03", desc: "Sinal reserva — Audi A4", v: "+ R$ 15.000", pos: true },
] as const;

const FLUXO_TABS = [
  { id: "FLUXO", label: "Fluxo de Caixa", Icon: Wallet },
  { id: "REL", label: "Relatórios", Icon: FileSpreadsheet },
  { id: "FEC", label: "Fechamento por carro", Icon: Car },
 ] as const;

export default function MockFinanceiroShowcase() {
  return (
    <div
      className="pointer-events-none flex h-full min-h-[200px] select-none flex-col"
      aria-label="Prévia fictícia do Financeiro (layout NoWey)"
    >
      <AppShellMock highlight="financeiro">
        <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden bg-background p-2 text-foreground md:p-3">
          <header className="mb-3 flex flex-col justify-between gap-2 md:flex-row md:items-center">
            <div>
              <h1 className="text-sm font-black tracking-tight text-foreground md:text-base">Financeiro</h1>
              <p className="mt-0.5 text-[10px] font-medium text-muted-foreground">
                Gerencie o fluxo de caixa e acompanhe os resultados da loja.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex h-8 items-center gap-1 rounded-xl border border-border bg-card px-2 text-[9px] text-muted-foreground">
                <Filter className="h-3 w-3" aria-hidden />
                Mês atual
              </span>
              <span className="inline-flex h-8 items-center gap-1 rounded-xl border border-border px-2 text-[9px]">
                <Download className="h-3 w-3" aria-hidden />
                PDF
              </span>
            </div>
          </header>

          <div className="mb-3 w-fit rounded-2xl border border-border bg-card p-0.5 shadow-lg">
            <div className="flex gap-0.5">
              {FLUXO_TABS.map((t, i) => (
                <span
                  key={t.id}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-xl px-2 py-1.5 text-[8px] font-bold md:px-3 md:py-2 md:text-[9px]",
                    i === 0
                      ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                      : "text-muted-foreground"
                  )}
                >
                  <t.Icon className="h-3 w-3 md:h-3.5 md:w-3.5" aria-hidden />
                  <span className="max-w-18 truncate sm:max-w-none">{t.label}</span>
                </span>
              ))}
            </div>
          </div>

          <section className="mb-3 grid grid-cols-2 gap-1.5 md:grid-cols-3 lg:grid-cols-6">
            {KPI_MOCK.map((kpi, idx) => (
              <Card
                key={idx}
                className="flex flex-col items-start gap-1.5 rounded-2xl border border-border bg-card p-2 shadow-sm md:gap-2 md:p-3 md:rounded-3xl"
              >
                <div className={cn("rounded-lg p-1.5 md:rounded-xl md:p-2.5", kpi.bg)}>
                  <kpi.icon className={cn("h-3.5 w-3.5 md:h-5 md:w-5", kpi.color)} aria-hidden />
                </div>
                <div>
                  <h3 className="text-xs font-bold tracking-tight text-foreground md:text-lg">{kpi.value}</h3>
                  <p className="text-[8px] font-medium text-muted-foreground md:text-xs">{kpi.label}</p>
                </div>
              </Card>
            ))}
          </section>

          <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
            <div className="space-y-2 lg:col-span-2">
              <div className="rounded-2xl border border-border bg-card p-2 shadow-sm">
                <p className="mb-2 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Receita x despesas
                </p>
                <div className="flex h-16 items-end gap-0.5 md:h-20 md:gap-1">
                  {BAR_HEIGHTS.map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ delay: i * 0.04, duration: 0.35, ease: "easeOut" }}
                      className={cn(
                        "min-w-[3px] flex-1 rounded-t-sm",
                        i === 6 ? "bg-primary" : "bg-primary/30"
                      )}
                      aria-hidden
                    />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card">
                <p className="border-b border-border px-2 py-1.5 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Lançamentos
                </p>
                <ul className="divide-y divide-border/60">
                  {LANCS.map((l) => (
                    <li key={l.desc} className="flex items-start justify-between gap-2 px-2 py-1.5">
                      <div className="min-w-0">
                        <span className="text-[8px] text-muted-foreground">{l.d}</span>
                        <p className="truncate text-[9px] text-foreground">{l.desc}</p>
                      </div>
                      <span
                        className={cn(
                          "shrink-0 text-[9px] font-bold tabular-nums",
                          l.pos ? "text-emerald-500" : "text-red-500"
                        )}
                      >
                        {l.v}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 p-2">
              <p className="mb-1 text-[9px] font-bold text-foreground">Novo lançamento</p>
              <div className="space-y-1.5">
                <div className="h-7 rounded-xl border border-border bg-card/80" />
                <div className="h-7 rounded-xl border border-border bg-card/80" />
                <div className="mt-1 h-8 rounded-xl bg-primary/90 text-center text-[9px] font-bold leading-8 text-primary-foreground">
                  Adicionar
                </div>
              </div>
            </div>
          </div>

          <p className="mt-2 text-[8px] text-muted-foreground">Interface de demonstração — dados fictícios.</p>
        </div>
      </AppShellMock>
    </div>
  );
}
